import { omit, without, some } from 'lodash';
import { batchActions } from './utils';
import { setData, unsetData } from './data';
import { setStatus, unsetStatus } from './status';
import {
  LOADING,
  ERROR,
  UNAUTHORIZED,
  CONFLICT,
  SUBMIT_SUCCESS,
  selectFeedValidationStatus,
  selectFeedSubmissionStatus,
  selectCategoriesState,
} from './selectors';
import { CATEGORIES_KEY, COUNTRIES_KEY, LANGUAGES_KEY, AGGREGATORS_KEY } from '../lib/constants';
import { trackEvent } from '../lib/Tracking';
import { poll, toJSON, PODCASTERS_SERVICE } from '../lib/Request';
import { Thunk, Podcast, SelectedCategory } from '../types';

const FORM_DATA_KEYS = [CATEGORIES_KEY, LANGUAGES_KEY, COUNTRIES_KEY, AGGREGATORS_KEY];

/* eslint-enable */

export const validateFeed = (feedUrl: string): Thunk => async (dispatch, getState, { request }) => {
  const key = 'rssFeed';
  /* istanbul ignore next */
  if (selectFeedValidationStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'submission/VALIDATE_FEED_REQUEST'));

  const { token }: { token: string } = await request
    .post(`${PODCASTERS_SERVICE}/v0/feeds/evaluate`, { feedUrl })
    .then(toJSON);

  try {
    const data = await poll(() =>
      request.get(`${PODCASTERS_SERVICE}/v0/feeds/evaluate/${token}`).then(toJSON),
    );

    dispatch(
      batchActions(
        [setData({ [key]: data }), unsetStatus([key])],
        'submission/VALIDATE_FEED_SUCCESS',
      ),
    );
    const trackingLabel = !data.errors ? 'success' : data.errors.toString();
    trackEvent({ category: 'submission', action: 'validate', label: trackingLabel });
  } catch (err) {
    if (err.message === '401') {
      dispatch(setStatus({ [key]: UNAUTHORIZED }, 'submission/VALIDATE_FEED_UNAUTHORIZED'));
    } else {
      dispatch(
        batchActions(
          [setData({ [key]: { errors: ['UNKNOWN_ERROR'] } as Podcast }), unsetStatus([key])],
          'submission/VALIDATE_FEED_SERVER_ERROR',
        ),
      );
      trackEvent({ category: 'submission', action: 'validate', label: 'server error' });
    }
  }
};

export const validateFeedAllowingExistingShows = (feedUrl: string): Thunk => async (
  dispatch,
  getState,
  { request },
) => {
  const key = 'rssFeed';
  /* istanbul ignore next */
  if (selectFeedValidationStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'submission/VALIDATE_FEED_REQUEST'));

  const { token }: { token: string } = await request
    .post(`${PODCASTERS_SERVICE}/v0/feeds/evaluate`, { feedUrl })
    .then(toJSON);

  try {
    const data: Podcast = await poll(() =>
      request.get(`${PODCASTERS_SERVICE}/v0/feeds/evaluate/${token}`).then(toJSON),
    );

    data.errors = data.errors || [];

    // if is an Anchor email address
    if (/^po.*anchor\.fm$/.test(data.email)) {
      data.errors.push('UNSUPPORTED_EMAIL_ANCHOR');
    }

    // if is a Soundlcloud email address
    if (data.email === 'feeds@soundcloud.com') {
      data.errors.push('UNSUPPORTED_EMAIL_SOUNDCLOUD');
    }

    dispatch(
      batchActions(
        [setData({ [key]: data }), unsetStatus([key])],
        'submission/VALIDATE_FEED_SUCCESS',
      ),
    );
    // istanbul ignore next
    const trackingLabel = !data.errors ? 'success' : data.errors.toString();
    trackEvent({ category: 'submission', action: 'validate', label: trackingLabel });
  } catch (err) {
    if (err.message === '401') {
      dispatch(setStatus({ [key]: UNAUTHORIZED }, 'submission/VALIDATE_FEED_UNAUTHORIZED'));
    } else {
      dispatch(
        batchActions(
          // this really really needs to be refactored.
          [setData({ [key]: { errors: ['UNKNOWN_ERROR'] } as Podcast }), unsetStatus([key])],
          'submission/VALIDATE_FEED_SERVER_ERROR',
        ),
      );
      trackEvent({ category: 'submission', action: 'validate', label: 'server error' });
    }
  }
};

export const resetFeed = (): Thunk => async dispatch =>
  dispatch(unsetData(['rssFeed', 'submitFeed', ...FORM_DATA_KEYS], 'submission/RESET'));

export const submitFeed = (
  networkId: string,
  feedUrl: string,
  metadata: {
    countryOfOrigin: string;
    language: string;
    emailVerificationToken: string;
  },
): Thunk => async (dispatch, getState, { request }) => {
  const key = 'submitFeed';
  /* istanbul ignore next */
  if (selectFeedSubmissionStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'submission/SUBMIT_FEED_REQUEST'));

  /* istanbul ignore next */
  const categories = (selectCategoriesState(getState()) || []).map(c => omit(c, 'categoryKey'));

  const { token }: { token: string } = await request
    .post(`${PODCASTERS_SERVICE}/v0/networks/${networkId}/create`, {
      feedUrl: feedUrl.trim(),
      ...metadata,
      categories,
    })
    .then(toJSON);

  try {
    const data: { uri: string } = await poll(() =>
      request.get(`${PODCASTERS_SERVICE}/v0/networks/${networkId}/create/${token}`).then(toJSON),
    );

    dispatch(
      batchActions(
        [
          setData({ [key]: data.uri }),
          setStatus({ [key]: SUBMIT_SUCCESS }),
          unsetData([CATEGORIES_KEY]),
        ],
        'submission/SUBMIT_FEED_SUCCESS',
      ),
    );
    trackEvent({ category: 'submission', action: 'submit', label: 'success' });
  } catch (err) {
    if (err.message === '401') {
      dispatch(setStatus({ [key]: UNAUTHORIZED }, 'submission/SUBMIT_FEED_UNAUTHORIZED'));
      trackEvent({ category: 'submission', action: 'submit', label: UNAUTHORIZED });
    } else if (err.message === '409') {
      dispatch(setStatus({ [key]: CONFLICT }, 'submission/SUBMIT_FEED_CONFLICT'));
      trackEvent({ category: 'submission', action: 'submit', label: CONFLICT });
    } else {
      dispatch(setStatus({ [key]: ERROR }, 'submission/SUBMIT_FEED_ERROR'));
      trackEvent({ category: 'submission', action: 'submit', label: ERROR });
    }
  }
};

export const updateFeed = (
  showId: string,
  metadata: {
    countryOfOrigin: string;
    language: string;
    emailVerificationToken: string;
  },
): Thunk => async (dispatch, getState, { request }) => {
  // uses the same key as `submitFeed` since we can use the same statuses as Submit and the UX should be the same
  const key = 'submitFeed';
  /* istanbul ignore next */
  if (selectFeedSubmissionStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'submission/UPDATE_FEED_REQUEST'));

  /* istanbul ignore next */
  const categories = (selectCategoriesState(getState()) || []).map(c => omit(c, 'categoryKey'));
  try {
    await request.put(`${PODCASTERS_SERVICE}/v0/shows/${showId}/metadata`, {
      ...metadata,
      categories,
    });

    dispatch(
      batchActions(
        [
          setData({ [key]: showId }),
          setStatus({ [key]: SUBMIT_SUCCESS }),
          unsetData([CATEGORIES_KEY]),
        ],
        'submission/UPDATE_FEED_SUCCESS',
      ),
    );
    trackEvent({ category: 'submission', action: 'update', label: 'success' });
  } catch (err) {
    /* istanbul ignore next */
    if (err.message === '401') {
      dispatch(setStatus({ [key]: UNAUTHORIZED }, 'submission/UPDATE_FEED_UNAUTHORIZED'));
      trackEvent({ category: 'submission', action: 'update', label: UNAUTHORIZED });
    } else {
      dispatch(setStatus({ [key]: ERROR }, 'submission/UPDATE_FEED_ERROR'));
      trackEvent({ category: 'submission', action: 'update', label: ERROR });
    }
  }
};

export const unsetSubmitFeedStatus = (): Thunk => async dispatch =>
  dispatch(unsetStatus(['submitFeed'], 'submission/SUBMIT_FEED_RESET'));

export const getCategoryErrors = (categories: SelectedCategory[]) =>
  categories.reduce<boolean[]>((acc, currentCategory, idx, all) => {
    if (idx === 0) acc.push(!currentCategory.value);
    else {
      const withoutCurrent = without(all, currentCategory);
      acc.push(some(withoutCurrent, c => c.value === currentCategory.value));
    }
    return acc;
  }, []);
